/* eslint-disable react/no-array-index-key */
// #useAuthUpdated
import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  Box,
  Divider,
  Link,
  makeStyles,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
} from "@material-ui/core";
import { Edit, School, Home, AccountCircle } from "@material-ui/icons";
import { useSB } from "../../contexts/SupabaseContext";
import { useCompany } from "../../contexts/CompanyContext";
import { useAuth } from "../../contexts/AuthContext";
import sqlToStandardDate from "../shared/functions/sqlToStandardDate";
import EditProfile from "./EditProfile";
import UpdateFreezeWrapper from "../admin-section/UpdateFreezeWrapper";
import SnapshotTiles from "./SnapshotTiles";
import { supabase } from "../../supabase";

// Create styles
const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
  },
  welcomeCard: {
    background: "linear-gradient(90deg, #0d253f 0%, #106b5e 100%)",
    color: "white",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
  },
  welcomeTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  zCode: {
    fontWeight: "normal",
  },
  statusCard: {
    marginBottom: theme.spacing(3),
    height: "100%",
    borderRadius: theme.spacing(1),
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
  },
  statusTitle: {
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  statusIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  verifiedChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: "bold",
  },
  notVerifiedChip: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    fontWeight: "bold",
  },
  tabsContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.1)",
    borderRadius: "16px",
  },
  infoSection: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  infoLabel: {
    color: theme.palette.text.secondary,
  },
  infoValue: {
    fontWeight: "bold",
    textTransform: "capitalize",
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1, 1, 0, 0),
    textTransform: "none",
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },
}));

/**
 * Component for rendering the content inside a tab.
 * @param {object} props - The properties for the TabPanel.
 * @param {React.ReactNode} props.children - The content to display inside the tab.
 * @param {number} props.value - The current active tab index.
 * @param {number} props.index - The index of this tab.
 * @returns {React.ReactElement} The TabPanel component.
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

/**
 * Generates props for the MUI Tabs component to make them accessible according to ARIA standards.
 * @param {number} index - The index of the tab.
 * @returns {object} Props for the MUI Tabs component.
 */
function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    "aria-controls": `profile-tabpanel-${index}`,
  };
}

/**
 * The ProfilePage component is a user profile page, which displays the user's profile information and allows them to edit it.
 * The page is divided into three sections: Personal Info, Address, and Services.
 * The Personal Info section displays the user's contact information, while the Address section displays the user's physical address.
 * The Services section displays the user's selected products and suppliers.
 * The page also includes action buttons to allow the user to edit their details or change their password.
 * The page is protected by the auth context, and only accessible if the user is logged in.
 */
export default function ProfilePage() {
  const classes = useStyles();
  const { GetTableWhere, updateRow } = useSB();
  const [dealer, setDealer] = useState();
  const [bankingDetails, setBankingDetails] = useState();
  const [editDealerOpen, setEditDealerOpen] = useState(false);
  const { productTypes, suppliers, updateFreeze } = useCompany();
  const { currentUser } = useAuth();
  const dealerId = currentUser?.id;
  const [tabValue, setTabValue] = useState(0);
  const [isLeader, setIsLeader] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  /**
   * Fetches the dealer document by its ID, as well as its parent dealer document by its ID.
   * If either document does not exist, throws an error.
   * If the user is an admin, the parent dealer document will be an empty array.
   * @param {string} subId - The ID of the dealer to fetch.
   * @returns {Promise<{dealer: object, parent: object}>} - The dealer and parent dealer documents.
   * @throws {Error} - If either document does not exist.
   */
  async function getSubDealerDetails(subId) {
    // Get the dealer document by its ID
    const dealerData = await GetTableWhere("users_master", ["id", "eq", subId]);

    // If the dealer document does not exist, throw an error
    if (!dealerData) {
      throw new Error(`Dealer with ID ${subId} not found`);
    }

    // Get the parent dealer document by its ID
    const parentData = await GetTableWhere("users_master", [
      "id",
      "eq",
      dealerData[0].parent_id,
    ]);

    // If the parent dealer document does not exist, throw an error
    if (!parentData && !currentUser.is_admin) {
      throw new Error(
        `Parent dealer with ID ${dealerData[0].parent_dealer} not found`
      );
    }
    // Return the dealer and parent dealer documents
    return {
      dealer: dealerData[0],
      parent: currentUser.is_admin ? [] : parentData[0],
    };
  }

  // Function to refresh the displayed details of the dealer
  const refreshDisplayedDetails = async () => {
    setDealer(null);
    const d = await getSubDealerDetails(dealerId);
    const fetchBankingDetails = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("refs_banking_details")
      .select("*")
      .eq("user_id", dealerId)
      .order("id", { ascending: false });

    if (fetchBankingDetails.data && fetchBankingDetails.data[0]?.length !== 0) {
      setBankingDetails(fetchBankingDetails.data[0]);
    }
    setDealer(d.dealer);
    setIsLeader(d.dealer.is_leader);
  };

  useEffect(() => {
    refreshDisplayedDetails();
  }, [productTypes, suppliers]);

  const handleIsLeader = event => {
    setIsLeader(event.target.checked);

    updateRow("users_master", dealerId, {
      is_leader: event.target.checked,
      is_leader_date: event.target.checked ? new Date() : null,
      last_updated_by_auth_id: currentUser.auth_id,
    });
  };

  // Function to parse the selected products from the user
  const parseSelectedProducts = () => {
    if (!productTypes) {
      return "No Products Selected";
    }
    return productTypes?.map(product => product.name).join(", ") || [];
  };

  // Function to parse the selected suppliers from the user
  const parseSelectedSuppliers = () => {
    if (!suppliers) {
      return "No Suppliers Selected";
    }

    return suppliers?.map(supplier => supplier.supplier_name).join(", ") || [];
  };

  if (!dealer) {
    return (
      <div className={classes.progressContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <UpdateFreezeWrapper />

      {/* Welcome Card */}
      <Paper className={classes.welcomeCard} elevation={0}>
        <Typography variant="h4" className={classes.welcomeTitle}>
          Profile - {dealer.first_name} {dealer.surname}
        </Typography>
        <Typography variant="subtitle1" className={classes.zCode}>
          Z-{dealer.id} • {dealer.company_name || "<User Organization />"}
        </Typography>
      </Paper>

      {/* SnapshotTiles */}
      <SnapshotTiles currentUser={currentUser} />

      {/* Tabs Section */}
      <Paper className={classes.tabsContainer}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label="Personal Info"
            icon={<AccountCircle />}
            {...a11yProps(0)}
          />
          <Tab label="Address" icon={<Home />} {...a11yProps(1)} />
          <Tab label="Services" icon={<School />} {...a11yProps(2)} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Z-Code
                </Typography>
                <Typography variant="body1">Z-{dealer.id}</Typography>
              </div>

              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  ID Number
                </Typography>
                <Typography variant="body1">
                  {dealer.identification_no || "9*********2"}
                </Typography>
              </div>

              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Phone Number
                </Typography>
                <Typography variant="body1">{dealer.phone}</Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Email
                </Typography>
                <Typography variant="body1">{dealer.email}</Typography>
              </div>

              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Date Joined
                </Typography>
                <Typography variant="body1">
                  {sqlToStandardDate(dealer.created_at)}
                </Typography>
              </div>

              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  T-Shirt Size
                </Typography>
                <Typography variant="body1">
                  {dealer.refs_shirt_size || "Not specified"}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ margin: "16px 0" }} />

              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Banking Information
                </Typography>
                <Typography variant="body2" className={classes.infoLabel}>
                  Payment Type
                </Typography>
                <Typography variant="body1" className={classes.infoValue}>
                  {dealer.refs_payment_type || "Not specified"}
                </Typography>

                {dealer.refs_payment_type === "eWallet" ? (
                  <>
                    <Box mt={1}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        eWallet Number
                      </Typography>
                      <Typography variant="body1" className={classes.infoValue}>
                        {dealer.phone}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        eWallet Link
                      </Typography>
                      <Typography
                        variant="body1"
                        className={[
                          classes.infoValue,
                          { textTransform: "none" },
                        ]}
                      >
                        {dealer.e_wallet_link || "Not specified"}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box mt={1}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        Bank
                      </Typography>
                      <Typography variant="body1" className={classes.infoValue}>
                        {bankingDetails?.bank || "Not specified"}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        Branch Code
                      </Typography>
                      <Typography variant="body1" className={classes.infoValue}>
                        {bankingDetails?.branch_code || "Not specified"}
                      </Typography>
                    </Box>
                    <Box mt={1}>
                      <Typography variant="body2" className={classes.infoLabel}>
                        Account Number
                      </Typography>
                      <Typography variant="body1" className={classes.infoValue}>
                        {bankingDetails?.account_number || "Not specified"}
                      </Typography>
                    </Box>
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Country
                </Typography>
                <Typography variant="body1">
                  {dealer.country || "Not specified"}
                </Typography>
              </div>{" "}
              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Street Address
                </Typography>
                <Typography variant="body1">
                  {dealer.street || "Not specified"}
                </Typography>
              </div>
              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Suburb
                </Typography>
                <Typography variant="body1">
                  {dealer.suburb || "Not specified"}
                </Typography>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  City
                </Typography>
                <Typography variant="body1">
                  {dealer.city || "Not specified"}
                </Typography>
              </div>

              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Province
                </Typography>
                <Typography variant="body1">
                  {dealer.province || "Not specified"}
                </Typography>
              </div>

              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Postal Code
                </Typography>
                <Typography variant="body1">
                  {dealer.postal_code || "Not specified"}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Selected Product Types
                </Typography>
                <Typography
                  variant="body1"
                  style={{ textTransform: "capitalize" }}
                >
                  {parseSelectedProducts()}
                </Typography>
              </div>

              <div className={classes.infoSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Selected Suppliers
                </Typography>
                <Typography
                  variant="body1"
                  style={{ textTransform: "capitalize" }}
                >
                  {parseSelectedSuppliers()}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      {!dealer.is_admin &&
        dealer.refs_user_tier_id > 2 &&
        !dealer.is_leader &&
        !dealer.is_leader_blocked && (
          <Card>
            <CardContent>
              <div style={{ marginTop: 16 }}>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={isLeader}
                      checked={isLeader}
                      onChange={handleIsLeader}
                      name="customSwitch"
                      color="primary"
                    />
                  }
                  label="Become A Leader"
                />
                <Typography variant="body1" style={{ marginTop: "16px" }}>
                  Dealer is{" "}
                  {isLeader && dealer.is_leader
                    ? `Leader since ${dealer.is_leader_date}`
                    : "not a Leader"}
                </Typography>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Action Buttons */}
      <Box className={classes.buttonContainer}>
        <Button
          disableElevation
          startIcon={<Edit />}
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={() => setEditDealerOpen(true)}
          disabled={updateFreeze}
        >
          Edit Your Details
        </Button>

        <Link href="/update-password" style={{ textDecoration: "none" }}>
          <Button
            disableElevation
            variant="contained"
            color="primary"
            className={classes.button}
          >
            Change Your Password
          </Button>
        </Link>
      </Box>

      {/* Edit Profile Dialog */}
      <EditProfile
        dialogIsOpen={editDealerOpen}
        closeDialog={() => {
          setEditDealerOpen(false);
          refreshDisplayedDetails();
        }}
        dealerProp={dealer}
        bankingDetails={bankingDetails}
        editing
      />
    </div>
  );
}
